import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Plus, Download, Eye, Pencil, Trash2, FileText, Receipt as ReceiptIcon, Package as PackageIcon, Settings, Wallet, AlertCircle, Search, RefreshCw, Filter, CheckCircle2, XCircle, FileX, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

const SAC_SUGGESTIONS = ['999312', '998311', '999314'];
const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-700',
  UNPAID: 'bg-[#F5B041]/10 text-[#F5B041]',
  PARTIALLY_PAID: 'bg-[#0A6F64]/10 text-[#0A6F64]',
  PAID: 'bg-[#2E8B57]/10 text-[#2E8B57]',
  OVERDUE: 'bg-[#D3544E]/10 text-[#D3544E]',
  CANCELLED: 'bg-[#D3544E]/10 text-[#D3544E]',
};

export default function InvoicingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = user?.role === 'ADMIN';
  const tab = searchParams.get('tab') || 'invoices';

  const setTab = (t) => setSearchParams({ tab: t });

  return (
    <div className="space-y-6" data-testid="invoicing-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Invoicing</h1>
          <p className="text-sm text-[#4A6B68] mt-0.5">Manage invoices, receipts, services and packages</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex flex-wrap gap-1 border-b border-[#E5F2F1]" data-testid="invoicing-tabs">
        {[
          { key: 'invoices', label: 'Invoices', icon: FileText },
          { key: 'catalog', label: 'Services & Products', icon: PackageIcon },
          { key: 'packages', label: 'Packages', icon: PackageIcon },
          { key: 'credit_notes', label: 'Credit Notes', icon: FileX },
          ...(isAdmin ? [{ key: 'settings', label: 'Tax Settings', icon: Settings }] : []),
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? 'border-[#0D8B7D] text-[#0D8B7D]' : 'border-transparent text-[#4A6B68] hover:text-[#1A3330]'}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {tab === 'invoices' && <InvoicesTab onCreate={() => navigate('/invoicing/invoice/new')} onView={(id) => navigate(`/invoicing/invoice/${id}`)} />}
      {tab === 'catalog' && <CatalogTab />}
      {tab === 'packages' && <PackagesTab />}
      {tab === 'credit_notes' && <CreditNotesTab />}
      {tab === 'settings' && isAdmin && <TaxSettingsTab />}
    </div>
  );
}

// ============== INVOICES TAB ==============
function InvoicesTab({ onCreate, onView }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total_invoiced: 0, total_collected: 0, total_outstanding: 0, overdue_count: 0 });
  const [filters, setFilters] = useState({ status: 'ALL', branch_id: 'ALL', date_from: '', date_to: '', search: '' });
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelInv, setCancelInv] = useState(null);
  const [cancelForm, setCancelForm] = useState({ amount: '', reason: '' });
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const cancelBusyRef = useRef(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'ALL') params.append('status', filters.status);
      if (isAdmin && filters.branch_id !== 'ALL') params.append('branch_id', filters.branch_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.search.trim()) params.append('search', filters.search.trim());
      const sumQs = isAdmin && filters.branch_id !== 'ALL' ? `?branch_id=${filters.branch_id}` : '';
      const [r, s] = await Promise.all([
        api.get(`/invoices?${params.toString()}`),
        api.get(`/invoices/summary${sumQs}`),
      ]);
      setItems(r.data.items || []);
      setSummary(s.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin) api.get('/branches').then(r => setBranches(r.data)).catch(() => {}); }, [isAdmin]);
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [filters]);

  const updateF = (k, v) => setFilters(p => ({ ...p, [k]: v }));

  const handleCancel = async (e) => {
    e.preventDefault();
    if (!cancelInv || cancelBusyRef.current) return;
    cancelBusyRef.current = true;
    setCancelSubmitting(true);
    setCancelError('');
    try {
      await api.post(`/invoices/${cancelInv._id}/credit-note`, {
        amount: parseFloat(cancelForm.amount),
        reason: cancelForm.reason,
      });
      setCancelInv(null);
      setCancelForm({ amount: '', reason: '' });
      await fetchData();
      toast.success('Invoice cancelled successfully.');
    } catch (err) {
      setCancelError(err.response?.data?.detail || 'Invoice could not be cancelled. Please try again.');
    } finally {
      cancelBusyRef.current = false;
      setCancelSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="invoices-tab">
      <div className="flex justify-end">
        <Button onClick={onCreate} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="create-invoice-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Invoice
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-invoiced" icon={Wallet} title="Total Invoiced" value={`Rs.${(summary.total_invoiced||0).toLocaleString()}`} sub={`${summary.invoice_count||0} invoices`} color="#0D8B7D" />
        <KpiCard testid="kpi-collected" icon={CheckCircle2} title="Total Collected" value={`Rs.${(summary.total_collected||0).toLocaleString()}`} sub="Payments received" color="#2E8B57" />
        <KpiCard testid="kpi-outstanding" icon={AlertCircle} title="Outstanding" value={`Rs.${(summary.total_outstanding||0).toLocaleString()}`} sub={`${summary.outstanding_count||0} unpaid`} color="#F5B041" />
        <KpiCard testid="kpi-overdue" icon={XCircle} title="Overdue" value={`${summary.overdue_count||0}`} sub="Past due date" color="#D3544E" />
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#E5F2F1] rounded-2xl">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-[#1A3330]">
            <Filter className="w-4 h-4" /><span className="text-sm font-semibold font-['Outfit']">Filters</span>
            <Button variant="ghost" size="sm" onClick={() => setFilters({ status: 'ALL', branch_id: 'ALL', date_from: '', date_to: '', search: '' })} className="ml-auto text-[#4A6B68]"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Reset</Button>
          </div>
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${isAdmin ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-3`}>
            <div>
              <Label className="text-xs text-[#4A6B68]">Search</Label>
              <Input placeholder="Client name, phone, email or invoice #" value={filters.search} onChange={e => updateF('search', e.target.value)} className="rounded-xl border-[#E5F2F1] h-9 text-sm" data-testid="inv-search" />
            </div>
            <div>
              <Label className="text-xs text-[#4A6B68]">Status</Label>
              <Select value={filters.status} onValueChange={v => updateF('status', v)}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1] h-9 text-sm" data-testid="inv-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="UNPAID">Unpaid</SelectItem>
                  <SelectItem value="PARTIALLY_PAID">Partially Paid</SelectItem>
                  <SelectItem value="PAID">Paid</SelectItem>
                  <SelectItem value="OVERDUE">Overdue</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#4A6B68]">From</Label>
              <Input type="date" value={filters.date_from} onChange={e => updateF('date_from', e.target.value)} className="rounded-xl border-[#E5F2F1] h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-[#4A6B68]">To</Label>
              <Input type="date" value={filters.date_to} onChange={e => updateF('date_to', e.target.value)} className="rounded-xl border-[#E5F2F1] h-9 text-sm" />
            </div>
            {isAdmin && (
              <div>
                <Label className="text-xs text-[#4A6B68]">Branch</Label>
                <Select value={filters.branch_id} onValueChange={v => updateF('branch_id', v)}>
                  <SelectTrigger className="rounded-xl border-[#E5F2F1] h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All branches</SelectItem>
                    {branches.map(b => <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5F2F1] rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4FAF9]">
              <TableHead className="text-[#4A6B68] text-xs">Invoice #</TableHead>
              <TableHead className="text-[#4A6B68] text-xs">Date</TableHead>
              <TableHead className="text-[#4A6B68] text-xs">Client</TableHead>
              {isAdmin && <TableHead className="text-[#4A6B68] text-xs">Branch</TableHead>}
              <TableHead className="text-[#4A6B68] text-xs text-right">Total</TableHead>
              <TableHead className="text-[#4A6B68] text-xs text-right">Paid</TableHead>
              <TableHead className="text-[#4A6B68] text-xs text-right">Balance</TableHead>
              <TableHead className="text-[#4A6B68] text-xs">Status</TableHead>
              <TableHead className="text-[#4A6B68] text-xs">Due Date</TableHead>
              <TableHead className="text-[#4A6B68] text-xs text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(inv => {
              const status = inv.is_overdue && (inv.status === 'UNPAID' || inv.status === 'PARTIALLY_PAID') ? 'OVERDUE' : inv.status;
              return (
                <TableRow key={inv._id} className="hover:bg-[#F4FAF9]" data-testid={`inv-row-${inv._id}`}>
                  <TableCell className="text-xs text-[#1A3330] font-medium">{inv.invoice_number || 'DRAFT'}</TableCell>
                  <TableCell className="text-xs text-[#4A6B68]">{inv.invoice_date}</TableCell>
                  <TableCell className="text-xs text-[#1A3330]">{inv.client_name || '-'}</TableCell>
                  {isAdmin && <TableCell className="text-xs text-[#4A6B68]">{inv.branch_name || '-'}</TableCell>}
                  <TableCell className="text-xs text-right text-[#1A3330] font-semibold">Rs.{(inv.grand_total||0).toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-right text-[#2E8B57]">Rs.{(inv.total_paid||0).toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-right text-[#D3544E] font-medium">Rs.{(inv.balance_due||0).toLocaleString()}</TableCell>
                  <TableCell><Badge className={`${STATUS_COLORS[status] || ''} text-[10px] border-0`}>{status?.replace('_', ' ')}</Badge></TableCell>
                  <TableCell className="text-xs text-[#4A6B68]">{inv.due_date || '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => onView(inv._id)} data-testid={`view-inv-${inv._id}`}><Eye className="w-4 h-4 text-[#0D8B7D]" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => downloadInvoicePdf(inv._id)} data-testid={`download-inv-${inv._id}`}><Download className="w-4 h-4 text-[#0D8B7D]" /></Button>
                      {isAdmin && inv.status !== 'CANCELLED' && (
                        <Button variant="ghost" size="sm" title="Cancel Invoice" onClick={() => { setCancelInv(inv); setCancelForm({ amount: inv.grand_total || 0, reason: '' }); }} data-testid={`cancel-inv-${inv._id}`}><FileX className="w-4 h-4 text-[#D3544E]" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {!loading && items.length === 0 && <div className="p-6 text-center text-sm text-[#4A6B68]">No invoices found.</div>}
        {loading && <div className="p-6 text-center text-sm text-[#4A6B68]">Loading...</div>}
      </Card>

      {/* Cancel Invoice modal (issues a credit note covering the full grand total) */}
      <Dialog open={!!cancelInv} onOpenChange={() => { if (!cancelSubmitting) { setCancelInv(null); setCancelError(''); } }}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="cancel-invoice-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#D3544E]">Cancel Invoice</DialogTitle></DialogHeader>
          {cancelInv && (
            <form onSubmit={handleCancel} className="space-y-3">
              <div className="bg-[#D3544E]/5 p-3 rounded-xl">
                <p className="text-sm font-medium">{cancelInv.invoice_number || 'DRAFT'} · Rs.{cancelInv.grand_total?.toLocaleString()}</p>
                <p className="text-[11px] text-[#4A6B68] mt-1">{cancelInv.is_draft ? 'Cancelling this draft will mark it as CANCELLED.' : 'A credit note will be generated to void the invoice.'} This action cannot be undone.</p>
              </div>
              <div><Label>Amount *</Label><Input type="number" step="0.01" value={cancelForm.amount} required onChange={e => setCancelForm({ ...cancelForm, amount: e.target.value })} className="rounded-xl border-[#E5F2F1]" data-testid="cancel-inv-amount" /></div>
              <div><Label>Reason *</Label><Textarea rows={3} value={cancelForm.reason} required onChange={e => setCancelForm({ ...cancelForm, reason: e.target.value })} className="rounded-xl border-[#E5F2F1]" data-testid="cancel-inv-reason" /></div>
              {cancelError && <p className="text-sm text-[#D3544E]" data-testid="cancel-inv-error">{cancelError}</p>}
              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={cancelSubmitting} className="flex-1 bg-[#D3544E] hover:bg-[#C04440] text-white rounded-xl" data-testid="confirm-cancel-inv-btn">{cancelSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cancelling...</>) : 'Cancel Invoice'}</Button>
                <Button type="button" variant="outline" disabled={cancelSubmitting} onClick={() => setCancelInv(null)} className="rounded-xl border-[#E5F2F1]">Close</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== Helpers ==============
async function downloadInvoicePdf(id) {
  const res = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a'); a.href = url; a.download = `invoice_${id}.pdf`; a.click();
  window.URL.revokeObjectURL(url);
}

async function downloadReceiptPdf(id) {
  const res = await api.get(`/receipts/${id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a'); a.href = url; a.download = `receipt_${id}.pdf`; a.click();
  window.URL.revokeObjectURL(url);
}

function KpiCard({ testid, icon: Icon, title, value, sub, color }) {
  return (
    <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid={testid}>
      <CardContent className="p-5 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${color}1A` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-[#4A6B68] font-medium">{title}</p>
          <p className="text-xl font-semibold text-[#1A3330] font-['Outfit']">{value}</p>
          <p className="text-[11px] text-[#4A6B68]">{sub}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ============== RECEIPTS TAB ==============
function ReceiptsTab() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', search: '' });
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    api.get(`/receipts?${params.toString()}`).then(r => setItems(r.data || [])).catch(() => {});
  }, [filters]);
  const filtered = items.filter(r => !filters.search || (r.client_name || '').toLowerCase().includes(filters.search.toLowerCase()) || (r.receipt_number || '').toLowerCase().includes(filters.search.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="receipts-tab">
      <Card className="bg-white border-[#E5F2F1] rounded-2xl">
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><Label className="text-xs">Search</Label><Input value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} className="rounded-xl border-[#E5F2F1] h-9 text-sm" placeholder="Receipt # or client" data-testid="receipt-search" /></div>
          <div><Label className="text-xs">From</Label><Input type="date" value={filters.date_from} onChange={e => setFilters({...filters, date_from: e.target.value})} className="rounded-xl border-[#E5F2F1] h-9 text-sm" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={filters.date_to} onChange={e => setFilters({...filters, date_to: e.target.value})} className="rounded-xl border-[#E5F2F1] h-9 text-sm" /></div>
        </CardContent>
      </Card>
      <Card className="bg-white border-[#E5F2F1] rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4FAF9]">
              <TableHead>Receipt #</TableHead><TableHead>Date</TableHead><TableHead>Client</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>Mode</TableHead>
              <TableHead>Linked Invoice</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(r => (
              <TableRow key={r._id} data-testid={`rcp-row-${r._id}`}>
                <TableCell className="text-xs font-medium">{r.receipt_number}</TableCell>
                <TableCell className="text-xs">{r.receipt_date}</TableCell>
                <TableCell className="text-xs">{r.client_name}</TableCell>
                <TableCell className="text-xs text-right font-semibold">Rs.{(r.amount||0).toLocaleString()}</TableCell>
                <TableCell><Badge className="bg-[#0D8B7D]/10 text-[#0D8B7D] text-[10px] border-0">{r.payment_mode}</Badge></TableCell>
                <TableCell className="text-xs">{r.invoice_number_ref || '-'}</TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => downloadReceiptPdf(r._id)} data-testid={`download-rcp-${r._id}`}><Download className="w-4 h-4 text-[#0D8B7D]" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filtered.length === 0 && <div className="p-6 text-center text-sm text-[#4A6B68]">No receipts.</div>}
      </Card>
    </div>
  );
}

// ============== CATALOG TAB (Services & Products) ==============
function CatalogTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetch = async () => {
    const params = new URLSearchParams();
    if (typeFilter !== 'ALL') params.append('item_type', typeFilter);
    if (search.trim()) params.append('search', search.trim());
    const r = await api.get(`/catalog?${params.toString()}`);
    setItems(r.data || []);
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [search, typeFilter]);

  const onSave = async (form) => {
    if (editing) await api.put(`/catalog/${editing._id}`, form);
    else await api.post('/catalog', form);
    setShowModal(false); setEditing(null); fetch();
  };
  const onDelete = async (id) => { if (!window.confirm('Delete this item?')) return; await api.delete(`/catalog/${id}`); fetch(); };

  return (
    <div className="space-y-4" data-testid="catalog-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-3 items-end flex-wrap">
          <div><Label className="text-xs">Search</Label><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name or code" className="rounded-xl border-[#E5F2F1] h-9 text-sm w-56" data-testid="cat-search" /></div>
          <div><Label className="text-xs">Type</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="rounded-xl border-[#E5F2F1] h-9 text-sm w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All</SelectItem>
                <SelectItem value="SERVICE">Services</SelectItem>
                <SelectItem value="PRODUCT">Products</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={() => { setEditing(null); setShowModal(true); }} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="add-catalog-btn"><Plus className="w-4 h-4 mr-2" /> Add Item</Button>
      </div>

      <Card className="bg-white border-[#E5F2F1] rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4FAF9]">
              <TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>SAC/HSN</TableHead>
              <TableHead>Unit</TableHead><TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">GST%</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(i => (
              <TableRow key={i._id} data-testid={`cat-row-${i._id}`}>
                <TableCell className="text-sm font-medium">{i.name}</TableCell>
                <TableCell><Badge className={`${i.item_type === 'SERVICE' ? 'bg-[#0D8B7D]/10 text-[#0D8B7D]' : 'bg-[#F5B041]/10 text-[#F5B041]'} text-[10px] border-0`}>{i.item_type}</Badge></TableCell>
                <TableCell className="text-xs">{i.code || '-'}</TableCell>
                <TableCell className="text-xs">{i.unit}</TableCell>
                <TableCell className="text-sm text-right font-semibold">Rs.{(i.unit_rate||0).toLocaleString()}</TableCell>
                <TableCell className="text-xs text-right">{i.gst_rate || 0}%</TableCell>
                <TableCell><Badge className={`${i.is_active ? 'bg-[#2E8B57]/10 text-[#2E8B57]' : 'bg-gray-100 text-gray-600'} text-[10px] border-0`}>{i.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(i); setShowModal(true); }} data-testid={`edit-cat-${i._id}`}><Pencil className="w-3.5 h-3.5 text-[#0D8B7D]" /></Button>
                  {isAdmin && <Button variant="ghost" size="sm" onClick={() => onDelete(i._id)} data-testid={`del-cat-${i._id}`}><Trash2 className="w-3.5 h-3.5 text-[#D3544E]" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {items.length === 0 && <div className="p-6 text-center text-sm text-[#4A6B68]">No items in catalog.</div>}
      </Card>

      {showModal && <CatalogModal initial={editing} onClose={() => { setShowModal(false); setEditing(null); }} onSave={onSave} />}
    </div>
  );
}

function CatalogModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial || {
    item_type: 'SERVICE', name: '', code: '', unit: 'Per Session', unit_rate: '',
    description: '', gst_rate: 0, is_active: true, branch_ids: [],
    stock_tracking: false, current_stock: 0
  });
  const isService = form.item_type === 'SERVICE';
  const units = isService ? ['Per Session', 'Per Hour', 'Per Day', 'Fixed'] : ['Piece', 'Set', 'Pack', 'Book'];

  const submit = async (e) => {
    e.preventDefault();
    await onSave({ ...form, unit_rate: parseFloat(form.unit_rate), gst_rate: parseFloat(form.gst_rate || 0) });
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white rounded-2xl" data-testid="catalog-modal">
        <DialogHeader><DialogTitle className="font-['Outfit']">{initial ? 'Edit' : 'Add'} Item</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.item_type} onValueChange={v => setForm({...form, item_type: v, unit: v === 'SERVICE' ? 'Per Session' : 'Piece'})}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="cat-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="SERVICE">Service</SelectItem><SelectItem value="PRODUCT">Product</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>{isService ? 'SAC Code' : 'HSN Code'}</Label>
              <Input value={form.code} onChange={e => setForm({...form, code: e.target.value})} placeholder={isService ? '999312' : 'e.g. 4901'} className="rounded-xl border-[#E5F2F1]" data-testid="cat-code" />
              {isService && <p className="text-[10px] text-[#4A6B68] mt-1">Common: {SAC_SUGGESTIONS.join(', ')}</p>}
            </div>
          </div>
          <div><Label>Name *</Label><Input value={form.name} required onChange={e => setForm({...form, name: e.target.value})} className="rounded-xl border-[#E5F2F1]" data-testid="cat-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Unit</Label>
              <Select value={form.unit} onValueChange={v => setForm({...form, unit: v})}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="cat-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{units.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Unit Rate (₹) *</Label><Input type="number" step="0.01" value={form.unit_rate} required onChange={e => setForm({...form, unit_rate: e.target.value})} className="rounded-xl border-[#E5F2F1]" data-testid="cat-rate" /></div>
          </div>
          <div>
            <Label>GST Rate %</Label>
            <Select value={String(form.gst_rate)} onValueChange={v => setForm({...form, gst_rate: parseFloat(v)})}>
              <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="cat-gst"><SelectValue /></SelectTrigger>
              <SelectContent>{[0,5,12,18,28].map(g => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-xl border-[#E5F2F1]" /></div>
          {!isService && (
            <div className="flex items-center gap-2">
              <Switch checked={form.stock_tracking} onCheckedChange={v => setForm({...form, stock_tracking: v})} data-testid="cat-stock-toggle" />
              <Label>Track Stock</Label>
              {form.stock_tracking && <Input type="number" value={form.current_stock} onChange={e => setForm({...form, current_stock: parseFloat(e.target.value || 0)})} className="rounded-xl border-[#E5F2F1] w-24" placeholder="Stock" />}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} />
            <Label>Active</Label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" className="flex-1 bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-cat-btn">{initial ? 'Save' : 'Add'}</Button>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl border-[#E5F2F1]">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============== PACKAGES TAB ==============
function PackagesTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetch = async () => {
    const r = await api.get('/packages'); setItems(r.data || []);
    const c = await api.get('/catalog?is_active=true'); setCatalog(c.data || []);
  };
  useEffect(() => { fetch(); }, []);

  const onSave = async (form) => {
    if (editing) await api.put(`/packages/${editing._id}`, form);
    else await api.post('/packages', form);
    setShowModal(false); setEditing(null); fetch();
  };
  const onDelete = async (id) => { if (!window.confirm('Delete this package?')) return; await api.delete(`/packages/${id}`); fetch(); };

  return (
    <div className="space-y-4" data-testid="packages-tab">
      <div className="flex justify-end">
        <Button onClick={() => { setEditing(null); setShowModal(true); }} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="add-package-btn"><Plus className="w-4 h-4 mr-2" /> New Package</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(p => (
          <Card key={p._id} className="bg-white border-[#E5F2F1] rounded-2xl" data-testid={`pkg-card-${p._id}`}>
            <CardContent className="p-5 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-base font-semibold text-[#1A3330] font-['Outfit']">{p.name}</p>
                  <p className="text-xs text-[#4A6B68] line-clamp-2">{p.description}</p>
                </div>
                <Badge className={`${p.is_active ? 'bg-[#2E8B57]/10 text-[#2E8B57]' : 'bg-gray-100'} text-[10px] border-0`}>{p.is_active ? 'Active' : 'Inactive'}</Badge>
              </div>
              <div className="text-xs text-[#4A6B68]">{p.items?.length || 0} items · Validity {p.validity_days || '—'} days</div>
              <div className="flex items-baseline gap-2 pt-2 border-t border-[#E5F2F1]">
                <span className="text-xl font-bold text-[#0D8B7D]">Rs.{p.selling_price?.toLocaleString()}</span>
                {p.savings > 0 && <span className="text-xs text-[#4A6B68]"><s>Rs.{p.package_total?.toLocaleString()}</s></span>}
              </div>
              {p.savings > 0 && <p className="text-xs text-[#2E8B57] font-medium">Client saves Rs.{p.savings?.toLocaleString()} ({Math.round((p.savings/(p.package_total||1))*100)}%)</p>}
              <div className="flex gap-1 pt-2">
                <Button variant="ghost" size="sm" onClick={() => { setEditing(p); setShowModal(true); }} data-testid={`edit-pkg-${p._id}`}><Pencil className="w-3.5 h-3.5 text-[#0D8B7D]" /></Button>
                {isAdmin && <Button variant="ghost" size="sm" onClick={() => onDelete(p._id)}><Trash2 className="w-3.5 h-3.5 text-[#D3544E]" /></Button>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {items.length === 0 && <Card className="bg-white border-[#E5F2F1] rounded-2xl"><CardContent className="p-6 text-center text-sm text-[#4A6B68]">No packages yet.</CardContent></Card>}
      {showModal && <PackageModal initial={editing} catalog={catalog} onClose={() => { setShowModal(false); setEditing(null); }} onSave={onSave} />}
    </div>
  );
}

function PackageModal({ initial, catalog, onClose, onSave }) {
  const [form, setForm] = useState(initial ? {
    name: initial.name, description: initial.description || '',
    items: initial.items || [], selling_price: initial.selling_price || 0,
    validity_days: initial.validity_days || 0, is_active: initial.is_active, branch_ids: initial.branch_ids || []
  } : {
    name: '', description: '', items: [], selling_price: 0, validity_days: 0, is_active: true, branch_ids: []
  });
  const total = useMemo(() => form.items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_rate || 0), 0), [form.items]);
  const savings = Math.max(0, total - (form.selling_price || 0));

  const addItem = (catId) => {
    const c = catalog.find(x => x._id === catId);
    if (!c) return;
    setForm({...form, items: [...form.items, { catalog_id: c._id, name: c.name, item_type: c.item_type, quantity: 1, unit_rate: c.unit_rate }]});
  };
  const updateItem = (idx, patch) => setForm({...form, items: form.items.map((it, i) => i === idx ? {...it, ...patch} : it)});
  const removeItem = (idx) => setForm({...form, items: form.items.filter((_, i) => i !== idx)});

  const submit = async (e) => {
    e.preventDefault();
    if (form.items.length === 0) { alert('Add at least one item'); return; }
    await onSave({ ...form, selling_price: parseFloat(form.selling_price || 0), validity_days: parseInt(form.validity_days || 0) });
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white rounded-2xl" data-testid="package-modal">
        <DialogHeader><DialogTitle className="font-['Outfit']">{initial ? 'Edit' : 'New'} Package</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 max-h-[75vh] overflow-y-auto pr-2">
          <div><Label>Package Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="pkg-name" /></div>
          <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-xl border-[#E5F2F1]" /></div>
          <div>
            <Label>Add Item</Label>
            <Select onValueChange={addItem}>
              <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="pkg-add-item"><SelectValue placeholder="Select service/product..." /></SelectTrigger>
              <SelectContent>{catalog.map(c => <SelectItem key={c._id} value={c._id}>{c.name} ({c.item_type}) — Rs.{c.unit_rate}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.items.length > 0 && (
            <div className="border border-[#E5F2F1] rounded-xl divide-y divide-[#E5F2F1]">
              {form.items.map((it, i) => (
                <div key={i} className="p-3 grid grid-cols-12 gap-2 items-center" data-testid={`pkg-item-${i}`}>
                  <div className="col-span-5 text-sm">{it.name} <Badge className="ml-1 text-[9px] border-0 bg-[#F4FAF9] text-[#4A6B68]">{it.item_type}</Badge></div>
                  <div className="col-span-2"><Input type="number" min="1" value={it.quantity} onChange={e => updateItem(i, {quantity: parseFloat(e.target.value || 1)})} className="h-8 text-sm rounded-lg" /></div>
                  <div className="col-span-3"><Input type="number" step="0.01" value={it.unit_rate} onChange={e => updateItem(i, {unit_rate: parseFloat(e.target.value || 0)})} className="h-8 text-sm rounded-lg" /></div>
                  <div className="col-span-1 text-right text-xs">Rs.{((it.quantity||0)*(it.unit_rate||0)).toLocaleString()}</div>
                  <div className="col-span-1"><Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)}><X className="w-3.5 h-3.5 text-[#D3544E]" /></Button></div>
                </div>
              ))}
            </div>
          )}
          <div className="bg-[#F4FAF9] rounded-xl p-3 space-y-1">
            <div className="flex justify-between text-sm"><span className="text-[#4A6B68]">Package Total (MRP)</span><span className="font-medium">Rs.{total.toLocaleString()}</span></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Selling Price *</Label><Input type="number" step="0.01" value={form.selling_price} onChange={e => setForm({...form, selling_price: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="pkg-selling-price" /></div>
              <div><Label className="text-xs">Validity (days)</Label><Input type="number" value={form.validity_days} onChange={e => setForm({...form, validity_days: e.target.value})} className="rounded-xl border-[#E5F2F1]" /></div>
            </div>
            {savings > 0 && <p className="text-sm text-[#2E8B57] font-medium" data-testid="pkg-savings">Client saves Rs.{savings.toLocaleString()} ({Math.round((savings/(total||1))*100)}%)</p>}
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} />
            <Label>Active</Label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" className="flex-1 bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-pkg-btn">{initial ? 'Save' : 'Create'}</Button>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl border-[#E5F2F1]">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// X icon helper
function X({ className }) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>; }

// ============== CREDIT NOTES TAB ==============
function CreditNotesTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get('/invoices/credit-notes/list').then(r => setItems(r.data)).catch(() => {}); }, []);
  return (
    <Card className="bg-white border-[#E5F2F1] rounded-2xl overflow-hidden" data-testid="credit-notes-tab">
      <Table>
        <TableHeader>
          <TableRow className="bg-[#F4FAF9]">
            <TableHead>CN #</TableHead><TableHead>Date</TableHead><TableHead>Client</TableHead>
            <TableHead>Against Invoice</TableHead><TableHead className="text-right">Amount</TableHead>
            <TableHead>Reason</TableHead><TableHead>Issued By</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map(c => (
            <TableRow key={c._id} data-testid={`cn-row-${c._id}`}>
              <TableCell className="text-xs font-medium">{c.credit_note_number}</TableCell>
              <TableCell className="text-xs">{c.issue_date}</TableCell>
              <TableCell className="text-xs">{c.client_name}</TableCell>
              <TableCell className="text-xs">{c.invoice_number_ref}</TableCell>
              <TableCell className="text-xs text-right text-[#D3544E] font-semibold">- Rs.{(c.amount||0).toLocaleString()}</TableCell>
              <TableCell className="text-xs max-w-[200px] truncate">{c.reason}</TableCell>
              <TableCell className="text-xs">{c.issued_by_name}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {items.length === 0 && <div className="p-6 text-center text-sm text-[#4A6B68]">No credit notes issued.</div>}
    </Card>
  );
}

// ============== TAX SETTINGS TAB ==============
function TaxSettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/tax-settings').then(r => setS(r.data)); }, []);
  if (!s) return <div className="p-6 text-sm text-[#4A6B68]">Loading...</div>;
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/tax-settings', s);
      setS(r.data);
      alert('Tax settings saved.');
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };
  return (
    <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid="tax-settings-tab">
      <CardContent className="p-6 space-y-4 max-w-2xl">
        <div className="flex items-center gap-3">
          <Switch checked={!!s.gst_enabled} onCheckedChange={v => setS({...s, gst_enabled: v})} data-testid="gst-toggle" />
          <Label>GST Enabled</Label>
          <p className="text-xs text-[#4A6B68]">Toggles tax calculation on all invoices and PDFs.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>GSTIN</Label><Input value={s.gstin || ''} onChange={e => setS({...s, gstin: e.target.value})} className="rounded-xl border-[#E5F2F1]" placeholder="33AABCT1234F1Z5" data-testid="gstin-input" /></div>
          <div><Label>Business Legal Name</Label><Input value={s.business_legal_name || ''} onChange={e => setS({...s, business_legal_name: e.target.value})} className="rounded-xl border-[#E5F2F1]" /></div>
          <div><Label>State Code</Label><Input value={s.state_code || ''} onChange={e => setS({...s, state_code: e.target.value})} className="rounded-xl border-[#E5F2F1]" /></div>
          <div><Label>State Name</Label><Input value={s.state_name || ''} onChange={e => setS({...s, state_name: e.target.value})} className="rounded-xl border-[#E5F2F1]" /></div>
          <div><Label>Default Payment Terms (days)</Label><Input type="number" value={s.default_payment_terms_days || 7} onChange={e => setS({...s, default_payment_terms_days: parseInt(e.target.value || 7)})} className="rounded-xl border-[#E5F2F1]" /></div>
        </div>
        <div><Label>Default Terms & Conditions</Label><Textarea rows={6} value={s.default_terms || ''} onChange={e => setS({...s, default_terms: e.target.value})} className="rounded-xl border-[#E5F2F1]" data-testid="default-terms" /></div>
        <Button onClick={save} disabled={saving} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-tax-settings">{saving ? 'Saving...' : 'Save Settings'}</Button>
      </CardContent>
    </Card>
  );
}
